import React, { useMemo } from "react";
import { useTheme } from "@material-ui/core";
import { RouteComponentProps } from "react-router-dom";
import {
  ExperimentState,
  PremierCollectionModuleProps,
  CorePremiumStaysCheckout,
} from "redmond";
import { useUserContext, isCorpTenant } from "@capone/common";
import { usePremiumStaysCheckoutProps } from "@capone/portal";

import config from "../../../utils/config";
import { colors as caponeColors } from "../../../utils/capone/colors";
import { colors as corpColors } from "../../../utils/capone-corporate/colors";
import ItineraryReviewBackground from "../../../assets/b2b/itinerary-review-background.jpeg";
import App from "../../../../../../cap1-modules/premier-collection-module/src/App";

interface IPremierCollectionModuleProps extends RouteComponentProps {
  language: string;
  experiments?: ExperimentState;
  showCoreHomepage?: boolean;
  core?: CorePremiumStaysCheckout;
}

const PremierCollectionModule = (props: IPremierCollectionModuleProps) => {
  const { language, experiments, showCoreHomepage, core, ...routerProps } =
    props;
  const { history } = routerProps;
  const theme = useTheme();

  const { sessionInfo, policies, logo, isRewardsDisabled } = useUserContext(
    config.TENANT
  );

  const moduleProps: PremierCollectionModuleProps & {
    showCoreHomepage?: boolean;
    skipPortalProvider?: boolean;
  } = useMemo(
    () => ({
      colors: isCorpTenant(config.TENANT) ? corpColors : caponeColors,
      experiments,
      language,
      theme,
      baseHistory: history,
      clientAssets: {
        logo: <img src={logo?.src} alt={logo?.alt} />,
        itineraryReviewBackground: ItineraryReviewBackground,
        sessionInfo,
        policies,
        isRewardsDisabled,
      },
      isAgentPortal: config.ptIsAgentPortal,
      showCoreHomepage,
      core,
      // This renders App directly inside the host's own tree, already nested
      // under BaseProviders' CaponePortal/CorporatePortal — App must not
      // mount a second one.
      skipPortalProvider: true,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isRewardsDisabled]
  );

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <App {...moduleProps} />;
};

export const LegacyPremierCollectionBook = (
  props: React.ComponentProps<typeof PremierCollectionModule>
) => {
  const checkoutProps = usePremiumStaysCheckoutProps();

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <PremierCollectionModule {...props} core={checkoutProps} />;
};

// Mounts the premier module for the replatformed VR book route. Unlike the
// hotel book wrapper, it doesn't inject `core` — the VR book page loads its own
// data from the URL.
export const LegacyPremierCollectionVRBook = (
  props: React.ComponentProps<typeof PremierCollectionModule>
) =>
  // eslint-disable-next-line react/jsx-props-no-spreading
  <PremierCollectionModule {...props} />;
